import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../components/AppIcon';

const API_URL = 'https://api.autocasses.com';

const COLORS = {
  primary: '#3b82f6',
  secondary: '#3b82f6',
  accent: '#60a5fa',
  light: '#f0f9ff',
  dark: '#1e3a8a',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  text: '#334155',
  lightText: '#64748b',
  background: '#f8fafc',
  white: '#ffffff',
  border: '#e2e8f0',
  bidBackground: '#fff7ed',
  questionBackground: '#f0f9ff',
  answerBackground: '#f7fee7',
};

// Fonction utilitaire pour formater les prix
const formatPriceFCFA = (price) => {
  if (!price && price !== 0) return '0 FCFA';
  return `${parseInt(price).toLocaleString('fr-FR')} FCFA`;
};

// Fonction pour calculer le temps restant
const calculateTimeLeft = (auction) => {
  if (!auction || !auction.date_enchere || !auction.temps) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  
  const startDate = new Date(auction.date_enchere);
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + parseInt(auction.temps));
  
  const now = new Date();
  const diff = endDate - now;
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, expired: false, endDate };
};

// Composant pour le timer
const AuctionTimer = ({ auction }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(auction));
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(auction));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [auction]);
  
  if (timeLeft.expired) {
    return (
      <div style={styles.timerExpired}>
        <Icon name="Flag" size={20} color={COLORS.danger} />
        <span style={styles.timerExpiredText}>
          Terminée
        </span>
      </div>
    );
  }
  
  return (
    <div style={styles.timerContainer}>
      {timeLeft.days > 0 && (
        <div style={styles.timeUnit}>
          <span style={styles.timeValue}>{timeLeft.days}</span>
          <span style={styles.timeLabel}>jours</span>
        </div>
      )}
      <div style={styles.timeUnit}>
        <span style={styles.timeValue}>{timeLeft.hours.toString().padStart(2, '0')}</span>
        <span style={styles.timeLabel}>heures</span>
      </div>
      <div style={styles.timeUnit}>
        <span style={styles.timeValue}>{timeLeft.minutes.toString().padStart(2, '0')}</span>
        <span style={styles.timeLabel}>minutes</span>
      </div>
      <div style={styles.timeUnit}>
        <span style={styles.timeValue}>{timeLeft.seconds.toString().padStart(2, '0')}</span>
        <span style={styles.timeLabel}>secondes</span>
      </div>
    </div>
  );
};

// Composant pour les offres
const BidItem = ({ bid, isWinning }) => {
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR') + ' à ' + 
             date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  return (
    <div 
      style={{
        ...styles.bidItem,
        ...(isWinning ? styles.winningBid : {})
      }}
    >
      <div style={styles.bidderInfo}>
        <div style={styles.bidderAvatar}>
          <span style={styles.bidderAvatarText}>
            {bid.offreuser?.nom?.charAt(0)?.toUpperCase() || 
             (bid.userId ? `U${bid.userId}`.charAt(0) : '?')}
          </span>
        </div>
        <div style={styles.bidderDetails}>
          <span style={styles.bidderName}>
            {bid.offreuser?.nom || `Utilisateur ${bid.userId}`}
          </span>
          <span style={styles.bidTime}>
            {formatDate(bid.createdAt)}
          </span>
        </div>
      </div>
      <span style={styles.bidAmount}>
        {formatPriceFCFA(bid.prix)}
      </span>
    </div>
  );
};

// Modal pour faire une offre
const BidModal = ({ visible, onClose, onConfirm, currentPrice, minBid, pas }) => {
  const [bidAmount, setBidAmount] = useState('');
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (minBid && !bidAmount) {
      setBidAmount(minBid.toString());
    }
  }, [minBid]);
  
  const increaseBid = () => {
    const current = parseFloat(bidAmount) || minBid;
    const newBid = current + (pas || 1000);
    setBidAmount(newBid.toString());
  };
  
  const decreaseBid = () => {
    const current = parseFloat(bidAmount) || minBid;
    const newBid = Math.max(minBid, current - (pas || 1000));
    setBidAmount(newBid.toString());
  };
  
  const handleConfirm = async () => {
    const bidAmountNum = parseFloat(bidAmount.replace(/\s/g, '').replace(',', '.'));
    
    if (!bidAmount || isNaN(bidAmountNum) || bidAmountNum <= 0) {
      alert('Offre invalide', 'Veuillez entrer un montant valide');
      return;
    }
    
    if (bidAmountNum <= currentPrice) {
      alert('Offre trop basse', `Votre offre doit être supérieure à ${formatPriceFCFA(currentPrice)}`);
      return;
    }
    
    if (minBid && bidAmountNum < minBid) {
      alert('Offre insuffisante', `L'offre minimum est de ${formatPriceFCFA(minBid)}`);
      return;
    }
    
    setLoading(true);
    try {
      await onConfirm(bidAmountNum);
      setBidAmount('');
      onClose();
    } catch (error) {
      alert('Erreur', error.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (!visible) return null;
  
  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            Faire une offre
          </h3>
          <button onClick={onClose} style={styles.modalCloseButton}>
            <Icon name="X" size={24} color={COLORS.text} />
          </button>
        </div>
        
        <div style={styles.priceInfo}>
          <span style={styles.priceLabel}>
            Prix actuel
          </span>
          <span style={styles.currentPrice}>
            {formatPriceFCFA(currentPrice)}
          </span>
        </div>
        
        <div style={styles.bidInputContainer}>
          <label style={styles.inputLabel}>
            Votre offre
          </label>
          <div style={styles.bidInputWrapper}>
            <button 
              style={styles.decreaseButton}
              onClick={decreaseBid}
              disabled={parseFloat(bidAmount) <= minBid}
            >
              <Icon name='Minus' size={24} color='white' />
            </button>
            
            <input
              type="text"
              style={styles.bidInput}
              value={bidAmount ? formatPriceFCFA(bidAmount).replace(' FCFA', '') : ''}
              onChange={(e) => {
                const text = e.target.value;
                const cleanText = text.replace(/\s/g, '').replace(/[^\d]/g, '');
                if (cleanText) {
                  const numValue = parseInt(cleanText);
                  setBidAmount(numValue.toString());
                } else {
                  setBidAmount('');
                }
              }}
              placeholder={`${formatPriceFCFA(minBid || currentPrice + 1000).replace(' FCFA', '')}`}
            />
            
            <button 
              style={styles.increaseButton}
              onClick={increaseBid}
            >
              <Icon name='Plus' size={24} color='white' />
            </button>
          </div>
          
          <div style={styles.stepInfo}>
            <Icon name="RefreshCw" size={12} color={COLORS.lightText} />
            <span style={styles.stepText}>
              Pas d'enchère: {formatPriceFCFA(pas || 1000)}
            </span>
          </div>
          
          <p style={styles.inputHint}>
            Votre offre doit être supérieure au prix actuel
          </p>
        </div>
        
        <div style={styles.modalActions}>
          <button
            style={styles.cancelButton}
            onClick={onClose}
            disabled={loading}
          >
            <span style={styles.cancelButtonText}>
              Annuler
            </span>
          </button>
          
          <button
            style={styles.confirmButton}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <div style={styles.loader}></div>
            ) : (
              <>
                <Icon name="Gavel" size={16} color={COLORS.white} />
                <span style={styles.confirmButtonText}>
                  Enchérir
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Page principale des détails
const DetailsEnchere = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auction: initialAuction } = location.state || {};
  
  const [auction, setAuction] = useState(initialAuction);
  const [loading, setLoading] = useState(true);
  const [bids, setBids] = useState([]);
  const [showBidModal, setShowBidModal] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (initialAuction?.id) {
      fetchAuctionDetails();
      checkAuth();
    }
  }, [initialAuction?.id]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('userToken');
      if (token) {
        const userId = localStorage.getItem('userId');
        if (userId) {
          setCurrentUserId(parseInt(userId));
        }
      }
    } catch (error) {
      console.log('Erreur vérification auth:', error);
    }
  };

  const fetchAuctionDetails = async () => {
    try {
      setLoading(true);
      
      const auctionResponse = await axios.get(`${API_URL}/api/produit/produits/${initialAuction.id}`);
      setAuction(auctionResponse.data);
      
      try {
        const token = localStorage.getItem('userToken');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const bidsResponse = await axios.get(`${API_URL}/api/offre/all`, {
          params: { enchereId: initialAuction.id },
          headers: headers
        });
        
        if (bidsResponse.data.success) {
          const sortedBids = bidsResponse.data.data.sort((a, b) => b.prix - a.prix);
          setBids(sortedBids);
        }
      } catch (bidsError) {
        console.log('Erreur récupération offres:', bidsError);
      }
    } catch (error) {
      console.error('Erreur récupération détails:', error);
      alert('Erreur', 'Impossible de charger les détails de l\'enchère');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handlePlaceBid = async (amount) => {
    try {
      const token = localStorage.getItem('userToken');
      if (!token) {
        alert(
          'Connexion requise',
          'Vous devez vous connecter pour enchérir'
        );
        return Promise.reject(new Error('Non authentifié'));
      }
      
      const response = await axios.post(`${API_URL}/api/offre/add`, {
        offre: amount,
        enchereId: auction.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        alert('Succès', response.data.message || 'Offre enregistrée avec succès');
        fetchAuctionDetails();
        return Promise.resolve();
      } else {
        throw new Error(response.data.message || 'Erreur lors de l\'enregistrement de l\'offre');
      }
    } catch (error) {
      let errorMessage = 'Une erreur est survenue';
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'Session expirée. Veuillez vous reconnecter.';
            localStorage.removeItem('userToken');
            break;
          case 403:
            errorMessage = 'Vous n\'avez pas la permission';
            break;
          case 400:
            errorMessage = 'Vous ne pouvez pas enchérir sur votre propre enchère';
            break;
          case 422:
            errorMessage = error.response.data?.message || 'Offre invalide';
            break;
          default:
            errorMessage = error.response.data?.message || `Erreur serveur (${error.response.status})`;
        }
      } else if (error.request) {
        errorMessage = 'Pas de connexion internet';
      } else {
        errorMessage = error.message;
      }
      
      alert('Erreur', errorMessage);
      throw new Error(errorMessage);
    }
  };
  
  const handleContactSeller = () => {
    if (auction.user?.telephone) {
      window.open(`tel:${auction.user.telephone}`, '_blank');
    } else if (auction.user?.email) {
      window.open(`mailto:${auction.user.email}`, '_blank');
    } else {
      alert(
        'Contacter le vendeur',
        'Aucun moyen de contact disponible'
      );
    }
  };
  
  const getCurrentPrice = () => {
    if (bids.length > 0) {
      return bids[0].prix;
    }
    return auction.prix || 0;
  };
  
  const getMinNextBid = () => {
    const current = getCurrentPrice();
    const pas = auction.pas;
    return current + pas;
  };
  
  const isAuctionActive = () => {
    return !calculateTimeLeft(auction).expired;
  };
  
  if (loading && !refreshing) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate(-1)} style={styles.backButton}>
            <Icon name="ArrowLeft" size={20} color={COLORS.white} />
            <span style={styles.backButtonText}>Retour</span>
          </button>
          <span style={styles.headerTitle}>Chargement...</span>
          <div style={styles.headerRightPlaceholder} />
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.loader}></div>
          <span style={styles.loadingText}>
            Chargement des détails...
          </span>
        </div>
      </div>
    );
  }
  
  if (!auction) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate(-1)} style={styles.backButton}>
            <Icon name="ArrowLeft" size={20} color={COLORS.white} />
            <span style={styles.backButtonText}>Retour</span>
          </button>
          <span style={styles.headerTitle}>Détails enchère</span>
          <div style={styles.headerRightPlaceholder} />
        </div>
        <div style={styles.errorContainer}>
          <Icon name="AlertTriangle" size={60} color={COLORS.danger} />
          <span style={styles.errorText}>
            Enchère non trouvée
          </span>
          <button 
            style={styles.backButtonError}
            onClick={() => navigate(-1)}
          >
            <span style={styles.backButtonText}>
              Retour à la liste
            </span>
          </button>
        </div>
      </div>
    );
  }
  
  const currentPrice = getCurrentPrice();
  const images = auction.photos || [];
  const isActive = isAuctionActive();
  const timeLeft = calculateTimeLeft(auction);
  const pas = auction.pas || 1000;
  
  return (
    <div style={styles.container}>
      {/* Header personnalisé avec bouton retour et titre */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          <Icon name="ArrowLeft" size={20} color={COLORS.white} />
          <span style={styles.backButtonText}>Retour</span>
        </button>
        <span style={styles.headerTitle} title={auction.titre}>
          {auction.titre}
        </span>
        <div style={styles.headerActions}>
          <button 
            onClick={handleContactSeller}
            style={styles.headerActionButton}
            title="Contacter le vendeur"
          >
            <Icon name="Phone" size={18} color={COLORS.white} />
          </button>
          {isActive && (
            <button
              style={styles.headerBidButton}
              onClick={() => setShowBidModal(true)}
              title="Faire une offre"
            >
              <Icon name="Gavel" size={16} color={COLORS.white} />
              <span style={styles.headerBidButtonText}>Faire une offre</span>
            </button>
          )}
        </div>
      </div>
      
      <div style={styles.contentContainer}>
        {/* Colonne gauche - Images et description */}
        <div style={styles.leftColumn}>
          {/* Galerie d'images */}
          <div style={styles.imageGallery}>
            {images.length > 0 ? (
              <>
                <img 
                  src={images[selectedImage]?.photo_url} 
                  style={styles.mainImage}
                  alt={auction.titre}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.style.cssText = `
                      width: 100%; 
                      height: 400px; 
                      background: #f1f5f9; 
                      display: flex; 
                      justify-content: center; 
                      align-items: center;
                      border-radius: 12px;
                    `;
                    fallback.innerHTML = '<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#64748b"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 1.1.9 2 2 2h15zM14 9.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/></svg>';
                    e.target.parentNode.appendChild(fallback);
                  }}
                />
                {images.length > 1 && (
                  <div style={styles.thumbnails}>
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        style={styles.thumbnailButton}
                      >
                        <img 
                          src={img.photo_url} 
                          style={{
                            ...styles.thumbnail,
                            ...(selectedImage === index ? styles.selectedThumbnail : {})
                          }}
                          alt={`Vue ${index + 1}`}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={styles.noImage}>
                <Icon name="Image" size={80} color={COLORS.lightText} />
                <span style={styles.noImageText}>
                  Aucune photo disponible
                </span>
              </div>
            )}
          </div>
          
          {/* Description */}
          <div style={styles.descriptionSection}>
            <h3 style={styles.sectionTitle}>Description</h3>
            <p style={styles.description}>
              {auction.description || 'Aucune description disponible'}
            </p>
            
            <div style={styles.detailsGrid}>
              <div style={styles.detailItem}>
                <Icon name="MapPin" size={20} color={COLORS.primary} />
                <div style={styles.detailContent}>
                  <span style={styles.detailLabel}>
                    Localisation
                  </span>
                  <span style={styles.detailValue}>
                    {auction.city || 'Non spécifié'}
                  </span>
                </div>
              </div>
              
              <div style={styles.detailItem}>
                <Icon name="Tag" size={20} color={COLORS.primary} />
                <div style={styles.detailContent}>
                  <span style={styles.detailLabel}>
                    État
                  </span>
                  <span style={styles.detailValue}>
                    {auction.etat || 'Non spécifié'}
                  </span>
                </div>
              </div>
              
              <div style={styles.detailItem}>
                <Icon name="Calendar" size={20} color={COLORS.primary} />
                <div style={styles.detailContent}>
                  <span style={styles.detailLabel}>
                    Date de début
                  </span>
                  <span style={styles.detailValue}>
                    {new Date(auction.date_enchere).toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
              
              <div style={styles.detailItem}>
                <Icon name="Clock" size={20} color={COLORS.primary} />
                <div style={styles.detailContent}>
                  <span style={styles.detailLabel}>
                    Fin estimée
                  </span>
                  <span style={styles.detailValue}>
                    {timeLeft.endDate 
                      ? new Date(timeLeft.endDate).toLocaleDateString('fr-FR', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) + ' à ' +
                        new Date(timeLeft.endDate).toLocaleTimeString('fr-FR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })
                      : 'Non définie'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Informations complémentaires */}
          <div style={styles.additionalInfo}>
            <h3 style={styles.sectionTitle}>Informations complémentaires</h3>
            
            <div style={styles.infoGrid}>
              {auction.region && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Région:
                  </span>
                  <span style={styles.infoValue}>{auction.region}</span>
                </div>
              )}
              
              {auction.pays && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Pays:
                  </span>
                  <span style={styles.infoValue}>{auction.pays}</span>
                </div>
              )}
              
              {auction.adresse_detail && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Adresse détaillée:
                  </span>
                  <span style={styles.infoValue}>{auction.adresse_detail}</span>
                </div>
              )}
              
              {auction.point_repere && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Point de repère:
                  </span>
                  <span style={styles.infoValue}>{auction.point_repere}</span>
                </div>
              )}
              
              {auction.garantie && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Garantie:
                  </span>
                  <span style={styles.infoValue}>{auction.garantie}</span>
                </div>
              )}
              
              {auction.negociable && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Négociable:
                  </span>
                  <span style={styles.infoValue}>
                    {auction.negociable === 'true' ? 'Oui' : 'Non'}
                  </span>
                </div>
              )}
              
              {auction.echange && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Échange possible:
                  </span>
                  <span style={styles.infoValue}>
                    {auction.echange === 'true' ? 'Oui' : 'Non'}
                  </span>
                </div>
              )}
              
              {pas > 0 && (
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>
                    Pas d'enchère:
                  </span>
                  <span style={styles.infoValue}>{formatPriceFCFA(pas)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Colonne droite - Informations d'enchère */}
        <div style={styles.rightColumn}>
          {/* Carte d'enchère */}
          <div style={styles.auctionCard}>
            {/* Badge d'état */}
            <div style={styles.statusBadge}>
              <div style={{...styles.badge, ...(!isActive ? styles.badgeExpired : {})}}>
                <Icon 
                  name={isActive ? "Zap" : "Flag"} 
                  size={16} 
                  color={COLORS.white} 
                />
                <span style={styles.badgeText}>
                  {isActive ? 'Enchère Active' : 'Enchère Terminée'}
                </span>
              </div>
            </div>
            
            {/* Titre et catégorie */}
            <div style={styles.auctionHeader}>
              <h2 style={styles.auctionTitle}>{auction.titre}</h2>
              <span style={styles.category}>
                {auction.categorie?.nom || 'Non catégorisé'}
              </span>
            </div>
            
            {/* Timer */}
            <div style={styles.timerSection}>
              <AuctionTimer auction={auction} />
              <span style={styles.timerInfo}>
                Durée: {auction.temps} heure{auction.temps > 1 ? 's' : ''}
              </span>
            </div>
            
            {/* Prix */}
            <div style={styles.priceSection}>
              <div style={styles.currentPriceContainer}>
                <span style={styles.priceLabel}>
                  Prix actuel
                </span>
                <span style={styles.currentPrice}>
                  {formatPriceFCFA(getCurrentPrice())}
                </span>
              </div>
              
              <div style={styles.startingPriceContainer}>
                <span style={styles.priceLabel}>
                  Prix de départ
                </span>
                <span style={styles.startingPrice}>
                  {formatPriceFCFA(auction.prix_enchere)}
                </span>
              </div>
            </div>
            
            {/* Pas d'enchère */}
            {pas > 0 && (
              <div style={styles.pasSection}>
                <Icon name="StepForward" size={18} color={COLORS.primary} />
                <span style={styles.pasText}>
                  Pas d'enchère: {formatPriceFCFA(pas)}
                </span>
              </div>
            )}
            
            {/* Bouton d'action */}
            {isActive ? (
              <button
                style={styles.bidButton}
                onClick={() => setShowBidModal(true)}
              >
                <Icon name="Gavel" size={20} color={COLORS.white} />
                <span style={styles.bidButtonText}>
                  Faire une offre
                </span>
              </button>
            ) : (
              <button
                style={styles.closedButton}
                disabled
              >
                <Icon name="Flag" size={20} color={COLORS.white} />
                <span style={styles.closedButtonText}>
                  Enchère terminée
                </span>
              </button>
            )}
            
            {/* Informations vendeur */}
            <div style={styles.sellerInfo}>
              <h4 style={styles.sellerTitle}>
                Vendeur
              </h4>
              <div style={styles.sellerDetails}>
                <div style={styles.sellerAvatar}>
                  <span style={styles.sellerAvatarText}>
                    {auction.user?.nom?.charAt(0)?.toUpperCase() || 'V'}
                  </span>
                </div>
                <div style={styles.sellerText}>
                  <span style={styles.sellerName}>
                    {auction.user?.nom || 'Vendeur'}
                  </span>
                  <span style={styles.sellerLocation}>
                    <Icon name="MapPin" size={14} color={COLORS.lightText} />
                    {' '}{auction.user?.ville || auction.pays || 'Localisation non spécifiée'}
                  </span>
                  {auction.user?.telephone && (
                    <button 
                      onClick={handleContactSeller}
                      style={styles.contactSellerButton}
                    >
                      <Icon name="Phone" size={14} color={COLORS.primary} />
                      <span>{auction.user.telephone}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Onglets pour offres */}
          <div style={styles.tabsContainer}>
            <div style={styles.tabs}>
              <button
                style={{...styles.tab, ...(activeTab === 'description' ? styles.activeTab : {})}}
                onClick={() => setActiveTab('description')}
              >
                <span style={{...styles.tabText, ...(activeTab === 'description' ? styles.activeTabText : {})}}>
                  Description
                </span>
              </button>
              <button
                style={{...styles.tab, ...(activeTab === 'offers' ? styles.activeTab : {})}}
                onClick={() => setActiveTab('offers')}
              >
                <div style={styles.tabWithBadge}>
                  <span style={{...styles.tabText, ...(activeTab === 'offers' ? styles.activeTabText : {})}}>
                    Offres ({bids.length})
                  </span>
                </div>
              </button>
            </div>
            
            {/* Liste des offres */}
            <div style={styles.offersSection}>
              {bids.length === 0 ? (
                <div style={styles.emptyBids}>
                  <Icon name="Gavel" size={50} color={COLORS.lightText} />
                  <span style={styles.emptyBidsText}>
                    Aucune offre pour le moment
                  </span>
                  <span style={styles.emptyBidsSubtext}>
                    Soyez le premier à enchérir!
                  </span>
                </div>
              ) : (
                <div style={styles.bidsList}>
                  <div style={styles.bidsHeader}>
                    <span style={styles.bidsHeaderText}>Enchèreur</span>
                    <span style={styles.bidsHeaderText}>Montant</span>
                    <span style={styles.bidsHeaderText}>Date</span>
                  </div>
                  {bids.map((item, index) => (
                    <BidItem key={item.id || index} bid={item} isWinning={index === 0} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal pour faire une offre */}
      <BidModal
        visible={showBidModal}
        onClose={() => setShowBidModal(false)}
        onConfirm={handlePlaceBid}
        currentPrice={currentPrice}
        minBid={getMinNextBid()}
        pas={pas}
      />
    </div>
  );
};

// Styles optimisés pour desktop
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: COLORS.background,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: COLORS.primary,
    padding: '16px 32px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: COLORS.white,
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    }
  },
  backButtonText: {
    fontSize: '16px',
    fontWeight: '500',
    color: COLORS.white,
  },
  headerTitle: {
    flex: 1,
    fontSize: '20px',
    fontWeight: '600',
    color: COLORS.white,
    textAlign: 'center',
    margin: '0 24px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerActionButton: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    color: COLORS.white,
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    }
  },
  headerBidButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: COLORS.success,
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#0da271',
    }
  },
  headerBidButtonText: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.white,
  },
  headerRightPlaceholder: {
    width: '36px',
  },
  
  // Layout principal
  contentContainer: {
    display: 'flex',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px',
    gap: '32px',
    '@media (max-width: 1200px)': {
      flexDirection: 'column',
    }
  },
  leftColumn: {
    flex: 2,
    minWidth: 0,
  },
  rightColumn: {
    flex: 1,
    minWidth: '380px',
    '@media (max-width: 1200px)': {
      minWidth: '100%',
    }
  },
  
  // Galerie d'images
  imageGallery: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  mainImage: {
    width: '100%',
    height: '400px',
    objectFit: 'contain',
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    marginBottom: '16px',
  },
  thumbnails: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    padding: '8px 0',
  },
  thumbnailButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  thumbnail: {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    objectFit: 'cover',
    border: '2px solid transparent',
  },
  selectedThumbnail: {
    borderColor: COLORS.primary,
  },
  noImage: {
    width: '100%',
    height: '300px',
    backgroundColor: '#f1f5f9',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '12px',
  },
  noImageText: {
    marginTop: '12px',
    color: COLORS.lightText,
    fontSize: '16px',
  },
  
  // Carte d'enchère
  auctionCard: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    top: '100px',
  },
  statusBadge: {
    marginBottom: '20px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: COLORS.success,
    padding: '8px 16px',
    borderRadius: '20px',
    gap: '8px',
  },
  badgeExpired: {
    backgroundColor: COLORS.danger,
  },
  badgeText: {
    color: COLORS.white,
    fontSize: '14px',
    fontWeight: '600',
  },
  auctionHeader: {
    marginBottom: '20px',
  },
  auctionTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: COLORS.text,
    margin: '0 0 8px 0',
    lineHeight: '1.3',
  },
  category: {
    fontSize: '14px',
    color: COLORS.primary,
    fontWeight: '500',
  },
  
  // Timer
  timerSection: {
    marginBottom: '24px',
    padding: '20px',
    backgroundColor: COLORS.light,
    borderRadius: '12px',
  },
  timerInfo: {
    fontSize: '14px',
    color: COLORS.lightText,
    textAlign: 'center',
    display: 'block',
    marginTop: '12px',
  },
  timerContainer: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  timerExpired: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
  },
  timerExpiredText: {
    fontSize: '18px',
    color: COLORS.danger,
    fontWeight: '600',
  },
  timeUnit: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    padding: '16px',
    borderRadius: '8px',
    minWidth: '70px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  },
  timeValue: {
    fontSize: '28px',
    fontWeight: '800',
    color: COLORS.primary,
  },
  timeLabel: {
    fontSize: '14px',
    color: COLORS.lightText,
    marginTop: '4px',
  },
  
  // Prix
  priceSection: {
    marginBottom: '20px',
  },
  currentPriceContainer: {
    marginBottom: '16px',
  },
  startingPriceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
    borderTop: '1px solid ' + COLORS.border,
  },
  priceLabel: {
    fontSize: '14px',
    color: COLORS.lightText,
    marginBottom: '8px',
    display: 'block',
  },
  currentPrice: {
    fontSize: '32px',
    fontWeight: '800',
    color: COLORS.primary,
    display: 'block',
  },
  startingPrice: {
    fontSize: '18px',
    color: COLORS.lightText,
    textDecoration: 'line-through',
  },
  
  // Pas d'enchère
  pasSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: COLORS.light,
    borderRadius: '8px',
    marginBottom: '20px',
  },
  pasText: {
    fontSize: '16px',
    color: COLORS.primary,
    fontWeight: '600',
  },
  
  // Boutons
  bidButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.primary,
    padding: '18px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    gap: '12px',
    marginBottom: '24px',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#2563eb',
    }
  },
  bidButtonText: {
    color: COLORS.white,
    fontSize: '18px',
    fontWeight: '600',
  },
  contactSellerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    marginTop: '8px',
    color: COLORS.primary,
    fontSize: '14px',
    ':hover': {
      textDecoration: 'underline',
    }
  },
  closedButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.danger,
    padding: '18px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'not-allowed',
    gap: '12px',
    marginBottom: '24px',
    opacity: '0.8',
  },
  closedButtonText: {
    color: COLORS.white,
    fontSize: '18px',
    fontWeight: '600',
  },
  
  // Informations vendeur
  sellerInfo: {
    paddingTop: '20px',
    borderTop: '1px solid ' + COLORS.border,
  },
  sellerTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.text,
    margin: '0 0 16px 0',
  },
  sellerDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  sellerAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: COLORS.primary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  sellerAvatarText: {
    color: COLORS.white,
    fontSize: '20px',
    fontWeight: '700',
  },
  sellerText: {
    flex: 1,
  },
  sellerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: '4px',
    display: 'block',
  },
  sellerLocation: {
    fontSize: '14px',
    color: COLORS.lightText,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  
  // Section description
  descriptionSection: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: COLORS.text,
    margin: '0 0 20px 0',
  },
  description: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: COLORS.text,
    marginBottom: '32px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginTop: '20px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    backgroundColor: COLORS.light,
    borderRadius: '12px',
  },
  detailContent: {
    flex: 1,
  },
  detailLabel: {
    fontSize: '14px',
    color: COLORS.lightText,
    marginBottom: '4px',
    display: 'block',
  },
  detailValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: COLORS.text,
    lineHeight: '1.4',
  },
  
  // Informations complémentaires
  additionalInfo: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  infoItem: {
    padding: '12px 0',
    borderBottom: '1px solid ' + COLORS.border,
    display: 'flex',
    justifyContent: 'space-between',
  },
  infoLabel: {
    fontSize: '14px',
    color: COLORS.lightText,
    fontWeight: '500',
  },
  infoValue: {
    fontSize: '14px',
    color: COLORS.text,
    fontWeight: '600',
  },
  
  // Onglets et offres
  tabsContainer: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  tabs: {
    display: 'flex',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid ' + COLORS.border,
  },
  tab: {
    flex: 1,
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: '#f1f5f9',
    }
  },
  activeTab: {
    backgroundColor: COLORS.white,
    borderBottom: '3px solid ' + COLORS.primary,
  },
  tabText: {
    fontSize: '16px',
    color: COLORS.lightText,
    fontWeight: '600',
  },
  activeTabText: {
    color: COLORS.primary,
  },
  tabWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  // Offres
  offersSection: {
    padding: '24px',
  },
  emptyBids: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 0',
  },
  emptyBidsText: {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.text,
    marginTop: '16px',
    marginBottom: '8px',
  },
  emptyBidsSubtext: {
    fontSize: '14px',
    color: COLORS.lightText,
    textAlign: 'center',
  },
  bidsList: {
    maxHeight: '500px',
    overflowY: 'auto',
  },
  bidsHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    padding: '12px 0',
    borderBottom: '2px solid ' + COLORS.border,
    marginBottom: '12px',
  },
  bidsHeaderText: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.lightText,
    textTransform: 'uppercase',
  },
  bidItem: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid ' + COLORS.border,
    ':hover': {
      backgroundColor: '#f8fafc',
    }
  },
  winningBid: {
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    padding: '16px',
    margin: '8px -8px',
    borderLeft: '4px solid ' + COLORS.primary,
  },
  bidderInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  bidderAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e0f2fe',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bidderAvatarText: {
    fontSize: '16px',
    color: COLORS.primary,
    fontWeight: '700',
  },
  bidderDetails: {
    flex: 1,
  },
  bidderName: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.text,
    display: 'block',
  },
  bidTime: {
    fontSize: '12px',
    color: COLORS.lightText,
    marginTop: '2px',
    display: 'block',
  },
  bidAmount: {
    fontSize: '16px',
    fontWeight: '700',
    color: COLORS.primary,
    textAlign: 'center',
  },
  
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: COLORS.white,
    borderRadius: '20px',
    padding: '32px',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: COLORS.text,
    margin: 0,
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    ':hover': {
      backgroundColor: '#f1f5f9',
    }
  },
  priceInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '32px',
    padding: '24px',
    backgroundColor: COLORS.light,
    borderRadius: '12px',
    flexDirection: 'column',
  },
  
  // États de chargement
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    padding: '20px',
  },
  loader: {
    border: '3px solid #f3f3f3',
    borderTop: '3px solid ' + COLORS.primary,
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '18px',
    color: COLORS.lightText,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    padding: '20px',
  },
  errorText: {
    fontSize: '20px',
    color: COLORS.danger,
    textAlign: 'center',
    margin: '24px 0',
  },
  backButtonError: {
    backgroundColor: COLORS.primary,
    padding: '14px 32px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    marginTop: '16px',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#2563eb',
    }
  },
};

export default DetailsEnchere;

// CSS global
const styleElement = document.createElement('style');
styleElement.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  * {
    box-sizing: border-box;
  }
  
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
  }
  
  button {
    cursor: pointer;
    font-family: inherit;
    outline: none;
  }
  
  button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  input {
    font-family: inherit;
    outline: none;
  }
  
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;
document.head.appendChild(styleElement);